import React, { useState, useEffect } from 'react';
import { Scan, Camera, Search, CheckCircle, AlertCircle, Link, Unlink, FileText, Users, BookOpen, Clock, User, History } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useGetHistoriqueAssociationsQuery, HistoriqueAssociationCopie } from '../../services/copiesApi';
import { useGetEtudiantsQuery, Etudiant as EtudiantApi } from '../../services/etudiantsApi';
import { Etudiant } from '../../types';

interface Copie {
  id: number;
  codeCopie: string;
  matiere: string;
  statut: 'NON_ASSOCIEE' | 'ASSOCIEE' | 'SCANNEE';
  dateScan?: string;
  etudiant?: {
    id: number;
    matricule: string;
    nom: string;
    prenoms?: string;
    email?: string;
    telephone?: string;
  };
  image?: string;
}


interface HistoriqueAssociation {
  id: number;
  copie_code: string;
  action: 'ASSOCIATION' | 'MODIFICATION' | 'SUPPRESSION';
  ancien_etudiant_matricule?: string;
  nouvel_etudiant_matricule?: string;
  effectue_par_nom: string;
  date_action: string;
  commentaire: string;
  adresse_ip?: string;
}

export const AssociationCopies: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [copies, setCopies] = useState<Copie[]>([]);
  
  // Récupérer les vraies données
  const { data: fetchedEtudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({});
  const { data: historique, isLoading: loadingHistorique } = useGetHistoriqueAssociationsQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCopie, setSelectedCopie] = useState<Copie | null>(null);
  const [selectedEtudiant, setSelectedEtudiant] = useState<EtudiantApi | null>(null);
  const [showAssociationModal, setShowAssociationModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [selectedCopieForHistory, setSelectedCopieForHistory] = useState<string>('');

  useEffect(() => {
    // Simuler le chargement des données
    setTimeout(() => {
      // Remplacer par de vraies données d'API si disponibles
      setCopies([]);
      setLoading(false);
    }, 1000);
  }, [fetchedEtudiants]);

  const handleScanCodeBarre = async () => {
    if (!fetchedEtudiants) return;
    
    setScanning(true);
    try {
      // Simuler le scan du code barre
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const randomCode = `A-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`;
      const existingCopy = copies.find(c => c.codeCopie === randomCode);
      
      if (existingCopy) {
        setSelectedCopie(existingCopy);
        setShowAssociationModal(true);
      } else {
        alert(`Code copie ${randomCode} non trouvé dans la base de données`);
      }
    } catch (error) {
      alert('Erreur lors du scan du code barre');
    } finally {
      setScanning(false);
    }
  };

  const handleAssociation = () => {
    if (!selectedCopie || !selectedEtudiant) {
      alert('Veuillez sélectionner une copie et un étudiant');
      return;
    }

    // Ajouter à l'historique (simulation)
    const nouvelleAction = {
      id: Date.now(),
      copie_code: selectedCopie.codeCopie,
      action: 'ASSOCIATION' as const,
      nouvel_etudiant_matricule: selectedEtudiant.matricule,
      effectue_par_nom: 'Utilisateur actuel', // À remplacer par l'utilisateur connecté
      date_action: new Date().toISOString(),
      commentaire: `Association automatique via scan/sélection`,
      adresse_ip: '192.168.1.100' // À remplacer par l'IP réelle
    };

    // Mettre à jour l'état local pour l'affichage
    const updatedCopies = copies.map(copie => 
      copie.id === selectedCopie.id 
        ? { 
            ...copie, 
            statut: 'ASSOCIEE' as const,
            etudiant: selectedEtudiant,
            dateScan: new Date().toISOString()
          }
        : copie
    );

    setCopies(updatedCopies);
    setShowAssociationModal(false);
    setSelectedCopie(null);
    setSelectedEtudiant(null);
    alert('Association réussie avec traçabilité !');
  };

  const handleDissociation = (copieId: number, copieCode: string) => {
    if (!copies) return;
    
    if ((window as any).confirm('Êtes-vous sûr de vouloir dissocier cette copie ?')) {
      // Ajouter à l'historique (simulation)
      const copie = copies.find(c => c.id === copieId);
      const nouvelleAction = {
        id: Date.now(),
        copie_code: copieCode,
        action: 'SUPPRESSION' as const,
        ancien_etudiant_matricule: copie?.etudiant?.matricule,
        effectue_par_nom: 'Utilisateur actuel',
        date_action: new Date().toISOString(),
        commentaire: 'Dissociation manuelle',
        adresse_ip: '192.168.1.100'
      };

      // Mettre à jour l'état local pour l'affichage
      const updatedCopies = copies.map(copie => 
        copie.id === copieId 
          ? { ...copie, statut: 'NON_ASSOCIEE' as const, etudiant: undefined, dateScan: undefined }
          : copie
      );

      setCopies(updatedCopies);
    }
  }; 

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'ASSOCIEE':
        return <Badge variant="success">Associée</Badge>;
      case 'SCANNEE':
        return <Badge variant="warning">Scannée</Badge>;
      case 'NON_ASSOCIEE':
        return <Badge variant="danger">Non associée</Badge>;
      default:
        return <Badge variant="info">{statut}</Badge>;
    }
  };

  const filteredCopies = (copies || []).filter(copie =>
    copie.codeCopie.toLowerCase().includes(searchQuery.toLowerCase()) ||
    copie.matiere.toLowerCase().includes(searchQuery.toLowerCase()) ||
    copie.etudiant?.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
    copie.etudiant?.prenoms?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statistiques = {
    total: (copies || []).length,
    associees: (copies || []).filter(c => c.statut === 'ASSOCIEE').length,
    nonAssociees: (copies || []).filter(c => c.statut === 'NON_ASSOCIEE').length,
    scannees: (copies || []).filter(c => c.statut === 'SCANNEE').length,
  };

  if (loading || loadingEtudiants || loadingHistorique) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Association Copies-Étudiants</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Association des copies d'examens aux étudiants via code barre</p>
        </div>
        <Button 
          variant="primary" 
          icon={<Scan size={20} />}
          onClick={handleScanCodeBarre}
          disabled={scanning}
        >
          {scanning ? <Spinner size="sm" /> : <Scan size={20} />}
          {scanning ? 'Scan...' : 'Scanner Code Barre'}
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
          <div className="flex items-center">
            <FileText className="text-blue-600 dark:text-blue-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400">Total Copies</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{statistiques.total}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700">
          <div className="flex items-center">
            <Link className="text-green-600 dark:text-green-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-green-600 dark:text-green-400">Associées</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-200">{statistiques.associees}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="flex items-center">
            <Unlink className="text-red-600 dark:text-red-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-red-600 dark:text-red-400">Non Associées</p>
              <p className="text-2xl font-bold text-red-900 dark:text-red-200">{statistiques.nonAssociees}</p>
            </div>
          </div>
        </Card>
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center">
            <Camera className="text-yellow-600 dark:text-yellow-400 mr-3" size={24} />
            <div>
              <p className="text-sm text-yellow-600 dark:text-yellow-400">Scannées</p>
              <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{statistiques.scannees}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recherche */}
      <Card>
        <div className="relative">
          <Search
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"
          />
          <Input
            placeholder="Rechercher une copie (code, matière, étudiant)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Liste des copies */}
      <Card>
        <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Liste des Copies</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code Copie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Matière</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Étudiant</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date Scan</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {filteredCopies.map((copie) => (
                <tr key={copie.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{copie.codeCopie}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{copie.matiere}</td>
                  <td className="px-4 py-3">
                    {copie.etudiant ? (
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{copie.etudiant.nom}</span>
                        <span className="text-gray-600 dark:text-gray-400 ml-1">{copie.etudiant.prenoms}</span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{copie.etudiant.matricule}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">Non associé</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatutBadge(copie.statut)}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-900 dark:text-gray-100">
                    {copie.dateScan ? new Date(copie.dateScan).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end space-x-2">
                      {copie.statut === 'NON_ASSOCIEE' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedCopie(copie);
                            setShowAssociationModal(true);
                          }}
                        >
                          Associer
                        </Button>
                      )}
                      {copie.etudiant && (
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleDissociation(copie.id, copie.codeCopie)}
                        >
                          Dissocier
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        icon={<FileText size={14} />}
                      >
                        Voir
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        icon={<History size={14} />}
                        onClick={() => {
                          setSelectedCopieForHistory(copie.codeCopie);
                          setShowHistoriqueModal(true);
                        }}
                      >
                        Historique
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal d'association */}
      {showAssociationModal && selectedCopie && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-screen overflow-y-auto dark:bg-gray-800">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                Associer la copie {selectedCopie.codeCopie}
              </h3>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Code Copie</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{selectedCopie.codeCopie}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Matière</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{selectedCopie.matiere}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rechercher un étudiant
                </label>
                <Input
                  placeholder="Matricule, nom ou prénoms..."
                  onChange={(e) => {
                    const query = e.target.value.toLowerCase();
                    const etudiant = fetchedEtudiants?.find(et => 
                      et.matricule.toLowerCase().includes(query) ||
                      et.nom.toLowerCase().includes(query) ||
                      et.prenoms?.toLowerCase().includes(query)
                    );
                    setSelectedEtudiant(etudiant as EtudiantApi || null);
                  }}
                />
              </div>

              {selectedEtudiant && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Étudiant sélectionné</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Matricule</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedEtudiant.matricule}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Nom complet</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedEtudiant.nom} {selectedEtudiant.prenoms}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Niveau</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedEtudiant.niveau}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400">Option</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{selectedEtudiant.option}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowAssociationModal(false);
                    setSelectedCopie(null);
                    setSelectedEtudiant(null);
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleAssociation}
                  disabled={!selectedEtudiant}
                >
                  Associer
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal d'historique */}
      {showHistoriqueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <Card className="w-full max-w-3xl max-h-screen overflow-y-auto dark:bg-gray-800">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center">
                  <History className="mr-2" size={20} />
                  Historique d'associations - {selectedCopieForHistory}
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setShowHistoriqueModal(false);
                    setSelectedCopieForHistory('');
                  }}
                >
                  Fermer
                </Button>
              </div>

              <div className="space-y-4">
                {Array.isArray(historique) && historique.length > 0 ? (
                  historique.map((action) => (
                    <div key={action.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <Badge 
                              variant={
                                action.action === 'ASSOCIATION' ? 'success' :
                                action.action === 'MODIFICATION' ? 'warning' :
                                'danger'
                              }
                              className="mr-2"
                            >
                              {action.action}
                            </Badge>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(action.date_action).toLocaleString('fr-FR')}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">Par:</p>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{action.effectue_par_nom}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-400">IP:</p>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{action.adresse_ip || 'N/A'}</p>
                            </div>
                          </div>

                          {action.ancien_etudiant_matricule && (
                            <div className="mt-2">
                              <p className="text-gray-600 dark:text-gray-400">Ancien étudiant:</p>
                              <p className="font-medium text-red-600 dark:text-red-400">{action.ancien_etudiant_matricule}</p>
                            </div>
                          )}

                          {action.nouvel_etudiant_matricule && (
                            <div className="mt-2">
                              <p className="text-gray-600 dark:text-gray-400">Nouvel étudiant:</p>
                              <p className="font-medium text-green-600 dark:text-green-400">{action.nouvel_etudiant_matricule}</p>
                            </div>
                          )}

                          {action.commentaire && (
                            <div className="mt-2">
                              <p className="text-gray-600 dark:text-gray-400">Commentaire:</p>
                              <p className="text-gray-800 dark:text-gray-200">{action.commentaire}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <History size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                    <p>Aucun historique trouvé pour cette copie</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Informations */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700">
        <div className="flex items-start">
          <Scan className="text-blue-600 dark:text-blue-400 mr-3 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Association des Copies</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• Scannez le code barre sur chaque copie d'examen</li>
              <li>• Associez automatiquement les copies aux étudiants concernés</li>
              <li>• Générez les relevés d'examens basés sur les copies associées</li>
              <li>• Dissociez les copies en cas d'erreur</li>
              <li>• Suivez l'état d'association de chaque copie</li>
              <li>• Consultez l'historique complet des modifications avec traçabilité</li>
              <li>• Toutes les actions sont enregistrées avec utilisateur, date et IP</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AssociationCopies;
